import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/data/supabase';
import * as z from 'zod';
import { sonner } from '@/components/ui/useSonner';

export class RequestError extends Error {
  constructor(
    message: string,
    public cause: string,
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

// make sure to always throw RequestErrors for easier handling
// in react-query
export async function requestHandler<
  R extends z.ZodTypeAny | undefined = undefined,
>(
  path: string,
  params: unknown, 
  responseSchema: R,
): Promise<R extends z.ZodTypeAny ? z.infer<R> : void> {
  try {
    const options: Record<string, unknown> = {};
    if (params) {
      options['body'] = params;
    }
    const { data, error } = await supabase.functions.invoke(
      `webapp-api/${path}`,
      options,
    );

    if (error instanceof FunctionsHttpError) {
      const errorResponse = error.context as Response;
      const errorBody = errorSchema.parse(await errorResponse.json());
      sonner().error(errorBody.message, {
        description: errorBody.cause,
        duration: 5000,
      });
      throw new RequestError(errorBody.message, errorBody.cause);
    } else if (error) {
      throw new RequestError(
        'Unknown Request Error',
        'An unknown error occurred while making the request.',
      );
    }

    if (typeof responseSchema === 'undefined') 
      return undefined as R extends z.ZodTypeAny ? z.infer<R> : void; 

    return responseSchema.parse(data);
  } catch (error) {
    if (error instanceof RequestError) throw error;
    if (error instanceof z.ZodError) {
      console.error("Zod Error:", error);
      throw new RequestError("Invalid response format", error.message);
    }

    throw new RequestError(
      'Unknown Error',
      'An unknown error occurred while making the request.',
    );
  }
}

const errorSchema = z.object({
  message: z.string(),
  cause: z.string(),
});